import { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { useDigitalLicensesWithLoading } from 'lib/api/hooks/digitalLicenses'
import { navigationEvent as createNavigationEvent } from 'layers/navigation/store/operations'
import { operations as lmsOperations } from 'store/lmsManager'
import {
  parseGradeAndProgram,
  getCourseLinks,
  coursesWithNewContentfulIds,
} from 'utils/lmsHelpers'

import { LESSONS_PROGRAM_CONFIG } from './constants'
import {
  processCourseTreeCollection,
  selectLessonsCurriculumClasses,
} from './data'

const findCurrentSiteV4 = ({ sites, activeSiteId }) => {
  if (!Array.isArray(sites) || activeSiteId == null) {
    return null
  }

  return (
    sites.find(
      site =>
        site?.id === activeSiteId || String(site?.id) === String(activeSiteId),
    ) || null
  )
}

export const useLessonsState = activeSiteId =>
  useSelector(state => {
    const { lmsManager, userContextManager, productManager } = state || {}

    return {
      currentSiteV4: findCurrentSiteV4({
        sites: state?.ssapiManager?.sites,
        activeSiteId,
      }),
      instructorData: lmsManager?.instructorData,
      lms: lmsManager,
      pathName: state?.router?.location?.pathname,
      productTabTitle: productManager?.productMap?.name || '',
      userAccess: state?.userAccessManager?.access,
      userContext: userContextManager,
      userProfile: state?.userProfileManager,
    }
  }, shallowEqual)

export const useLessonsActions = () => {
  const dispatch = useDispatch()

  const navigationEvent = useCallback(
    (...args) => dispatch(createNavigationEvent(...args)),
    [dispatch],
  )
  const createCourseInstanceV2ProgramFlex = useCallback(
    (...args) =>
      dispatch(lmsOperations.createCourseInstanceV2ProgramFlex(...args)),
    [dispatch],
  )
  const createCourseInstanceByCourse = useCallback(
    params => dispatch(lmsOperations.createCourseInstanceByCourse(params)),
    [dispatch],
  )
  const deletingCourseCreatedStatus = useCallback(
    (...args) => dispatch(lmsOperations.deletingCourseCreatedStatus(...args)),
    [dispatch],
  )
  const deleteCourseInstance = useCallback(
    (...args) => dispatch(lmsOperations.deleteCourseInstanceV2(...args)),
    [dispatch],
  )
  const fetchInstructorData = useCallback(
    (...args) => dispatch(lmsOperations.fetchInstructorProgramFlexGet(...args)),
    [dispatch],
  )
  const updateCourseInstance = useCallback(
    (...args) => dispatch(lmsOperations.updateCourseInstanceV2(...args)),
    [dispatch],
  )

  return {
    createCourseInstanceByCourse,
    createCourseInstanceV2ProgramFlex,
    deleteCourseInstance,
    deletingCourseCreatedStatus,
    fetchInstructorData,
    navigationEvent,
    updateCourseInstance,
  }
}

export const useLessonsPageData = ({
  courseManager,
  currentSite,
  instructorData,
  match,
  userContext,
}) => {
  // CreateClass needs license data before the modal opens.
  useDigitalLicensesWithLoading()

  const { program, programKey } = useMemo(
    () => parseGradeAndProgram(match?.url),
    [match?.url],
  )

  const classes = useMemo(
    () =>
      selectLessonsCurriculumClasses({
        instructorData,
        userContext,
        programKey,
      }),
    [instructorData, userContext, programKey],
  )

  const courseLinks = useMemo(
    () => getCourseLinks(coursesWithNewContentfulIds({ programType: program })),
    [program],
  )

  const courseTreesWithDisplayTitles = useMemo(
    () => processCourseTreeCollection(courseManager?.courseTrees),
    [courseManager?.courseTrees],
  )

  const lessonCompletionGoalValue = useMemo(() => {
    const preferencesKey =
      LESSONS_PROGRAM_CONFIG[programKey]?.sitePreferencesKey

    return preferencesKey
      ? currentSite?.[preferencesKey]?.lessonCompletionGoalValue || null
      : null
  }, [currentSite, programKey])

  return {
    classes,
    courseLinks,
    courseTreesWithDisplayTitles,
    lessonCompletionGoalValue,
    programKey,
  }
}

export const useAutoOpenTrigger = () => {
  const [autoOpen, setAutoOpen] = useState(false)
  const open = useCallback(() => setAutoOpen(true), [])

  useEffect(() => {
    if (autoOpen) {
      setAutoOpen(false)
    }
  }, [autoOpen])

  return {
    autoOpen,
    open,
  }
}
